import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import type { Update } from 'history';
import numeral from 'numeral';
import { Box, Container, Grid, Link, Typography } from "@mui/material";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Footer from "widgets/Footer/Footer";
import { usePolicyQuery } from 'entities/Policy/hooks/queries';
import { FormDataContext } from 'app/providers/formValues/lib/FormDataContext';
import { history } from 'app/providers/router/ui/NavigationProvider';
import { useJourneyQuery } from 'entities/ApplicationForm/hooks/queries';
import LogoHeader from 'features/LogoHeader/ui/LogoHeader';
import { useCampaign } from 'features/ThemeSettings/ui/ThemeSettings';

const ConfirmationPage = () => {
	const showWeDo = false;
	const data = useCampaign()
	const query = usePolicyQuery();
	const customerData = useJourneyQuery();
	const [email, setEmail] = useState("");
	const [coverageDates, setCoverageDates] = useState("");
	const [policyNumber, setPolicyNumber] = useState("");
	const [policyCost, setPolicyCost] = useState("");
	const [paidToday, setPaidToday] = useState("");
	const navigate = useNavigate();
	const { formData: { plan }, plan: currentPlan, policyStartDate, paymentData } = useContext(FormDataContext);
	const addMonth = plan.period === 4 ? 2 : 1;
	const nextBillingDay = dayjs(policyStartDate).add(addMonth, 'month').format('MM/DD/YYYY');

	useEffect(() => {
		if(query && customerData) {
			const postMessageData = {
				"referenceId": query.transactionDetails.referralId,
				"customerJourneyId": customerData.customerJourneyId,
				"event_name":"Policy Issued",
				"event_value":query.transactionDetails.policyId
			}
			window.parent.postMessage(postMessageData, document.location.ancestorOrigins[0])
		}
	}, []);

	useEffect(() => {
		if (!query) return;

		if (query.policyHolderDetails.insured.emailAddress && !email) {
			setEmail(query.policyHolderDetails.insured.emailAddress);
		}
		if (query.transactionDetails.policyEffectiveDate && !coverageDates) {
			const startDate = dayjs(query.transactionDetails.policyEffectiveDate).format('MM/DD/YYYY');
			const endDate = dayjs(query.transactionDetails.policyExpirationDate).format('MM/DD/YYYY');

			const result = startDate.concat(" to ", endDate);
			setCoverageDates(result);
		}
		if (query.transactionDetails.policyId && !policyNumber) {
			setPolicyNumber(query?.transactionDetails.policyId);
		}
		if (query.premiumDetails.premiumAmount && !policyCost) {
			setPolicyCost(`$${query?.premiumDetails.premiumAmount}`);
		}
		if (query.billingDetails.amount && !paidToday) {
			setPaidToday(`$${query?.billingDetails.amount}`);
		}
	}, [query, coverageDates, email, policyNumber, policyCost, paidToday]);

	const click = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, bytes?: string) => {
		e.preventDefault()
		if(!bytes) return
		const byteCharacters = atob(bytes);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const file = new Blob([byteArray], { type: 'application/pdf;base64' });
		const fileURL = URL.createObjectURL(file);
		window.open(fileURL);
	}

	useEffect(() => {
		history.listen((update: Update) => {
			if (update.action === "POP") {
				history.go(1);
			}
		});
		const client_id = localStorage.getItem('client_id');
		if(!client_id) {
			navigate("/error");
		}
		localStorage.removeItem('client_id');
	}, []);

	const generateText = useMemo(() => {
		if(data && data.partnerName.toLowerCase() === 'rate.com') {
			return `We appreciate your business and we are pleased to let you know your renters insurance policy has been 
			placed with Assurant. You can expect your policy documents to arrive in your mailbox within 3-5 days. `
		}
		return `You can officially cross renters insurance. And we’ll automatically renew your policy - so that’s even less to worry about.`
	}, [data]);

	const generateTitle = useMemo(() => {
		if(data && data.partnerName.toLowerCase() === 'rate.com') {
			return `Thank you for choosing Rate insurance,\n powered by REIN!`
		}
		return `Thanks for choosing REIN!`
	}, [data]);

	return (
		<Box>
			<Grid
				container
				spacing={0}
				sx={{ minHeight: '100vh' }}
			>
				<Grid size={12}>
					<Box sx={{ textAlign: 'center', position: 'relative' }} mt={5}>
						<LogoHeader />
					</Box>
					<Container maxWidth={"lg"} sx={{ mt: 5 }}>
						<Box display="flex" alignItems="flex-start" mb={2} justifyContent="center">
							<CheckCircleRoundedIcon
								sx={{ color: "primary.light", mr: 1, width: 50, height: 50 }}/>
							<Typography variant={"h3"} align={"center"} fontSize={42} sx={{ mb: 0, whiteSpace: 'pre-line' }}>{generateTitle}</Typography>
						</Box>
						<Typography variant={"subtitle2"} color="darkGrey.secondary" fontWeight={400}>
							{generateText}
							We sent a receipt with details on your coverage selections and what you paid today
							to <Link href={email} fontWeight={700}>{email || "Testmail@test.com"}</Link>.
							Didn’t receive it? <Link href="#" color="primary.light" fontWeight={700} underline="hover">
								Click here to resend the email
							</Link>.
							You’ll also see your documents in your inbox within 24 to 48 hours.
						</Typography>
						<Grid container justifyContent="center" spacing={1}>
							<Grid size={12}>
								<Box border="1px solid grey" borderColor="primary.light" sx={{ p: 2, mt: 4, backgroundColor: "background.default" }}>
									<Box display="flex" alignItems="center" mb={2}>
										<CheckCircleRoundedIcon
											sx={{ color: "primary.light", mr: 1, width: 30, height: 30 }}/>
										<Typography
											variant="h3"
											mb={0}
											fontWeight={800}
											fontSize={28}
											color="text.primary"
										>Renters Policy Summary</Typography>
									</Box>
									<Grid container spacing={2} columnSpacing={12}>
										<Grid size={{ md: 6, sm: 12, xs: 12 }} justifyContent="space-between" display="flex">
											<Typography variant="subtitle2" fontSize={16}>
												Annual Renters Insurance Cost
												<Typography variant="subtitle2" fontSize={12}>(Excluding Billing Fees)</Typography>
											</Typography>
											<Typography variant="subtitle2" fontSize={16}>{policyCost || "$259.03"}</Typography>
										</Grid>
										<Grid size={{ md: 6, sm: 12, xs: 12 }} justifyContent="space-between" display="flex">
											<Typography variant="subtitle2" fontSize={16}>Effective Date</Typography>
											<Typography variant="subtitle2" fontSize={16}>{coverageDates || "11/11/2022 to 11/11/2023"}</Typography>
										</Grid>
										<Grid size={{ md: 6, sm: 12, xs: 12 }} justifyContent="space-between" display="flex">
											<Typography variant="subtitle2" fontSize={16}>Your Policy Number</Typography>
											<Typography variant="subtitle2" fontSize={16}>{policyNumber || "REN1115089"}</Typography>
										</Grid>
										<Grid size={{ md: 6, sm: 12, xs: 12 }} justifyContent="space-between" display="flex">
											<Typography variant="subtitle2" fontSize={16}>Insurance Address</Typography>
											<Typography
												variant="subtitle2"
												fontSize={16}>{paymentData.fullAddress}</Typography>
										</Grid>
										<Grid size={12} mt={3}>
											{plan.period !== 1 && <Typography variant="subtitle2" fontSize={16} sx={{fontStyle: 'italic'}}>
												{`Your next payment of ${numeral(currentPlan.installmentAmount || currentPlan.downPaymentAmount || 0)
													.add(currentPlan.fee ?? 0).format('$0,0[.]00')} 
													will be charged automatically on ${nextBillingDay}.`
												}
											</Typography>}
										</Grid>
										{query?.appBytes && (
											<Grid size={12} mt={0}>
												<Link
													href={'#'}
													onClick={(e) => click(e,query?.appBytes)}
													color="primary.light"
													underline="hover"
													target="_blank"
												>
													Download a copy of your Renters Policy Summary</Link>
											</Grid>
										)}
										<Grid size={12}>
											Questions? Visit <Link
												href="https://manage.myassurantpolicy.com/"
												color="primary.light"
												underline="hover">
												manage.myassurantpolicy.com
											</Link>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							<Grid size={12}>
								<Box
									border="1px solid grey"
									borderColor="primary.light"
									sx={{ p: 2, mt: 2, backgroundColor: "background.default" }}
									display="flex"
									justifyContent="space-between"
									alignItems="center"
								>
									<Box display="flex" alignItems="center">
										<CheckCircleRoundedIcon
											sx={{ color: "primary.light", mr: 1, width: 30, height: 30 }}/>
										<Typography
											variant="h3"
											mb={0}
											fontWeight={800}
											fontSize={32}
											color="text.primary"
										> Total Paid Today</Typography>
									</Box>
									<Typography
										variant="h3"
										mb={0}
										fontWeight={800}
										fontSize={32}
										color="text.primary"
									>{paidToday || "$259.03"}</Typography>
								</Box>
							</Grid>
							{showWeDo && (
								<Grid size={{ md: 3 }} flexDirection="column" display="flex">
									<Box
										border="1px solid grey"
										borderColor="greyBlue.main"
										sx={{ p: 2, mt: 2 }}
										display="flex"
										justifyContent="space-between"
										alignItems="center"
									>
										<Box display="flex" flexDirection="column">
											<Typography
												variant="h4"
												fontWeight={800}
											>Need extra coverage for certain items?</Typography>
											<Typography
												variant={"subtitle2"} color="text.primary" fontWeight={400}
											>
												<Link href={"#"} color="primary.light" underline="hover">
													Fill out this form
												</Link> to cover your more expensive items( like jewelry, fine art and/or furs).
											</Typography>
										</Box>
									</Box>
									<Typography
										variant="h4"
										textAlign="center"
										mt={2}
										fontWeight={800}
									>How'd We Do?</Typography>
								</Grid>
							)}
						</Grid>
						<Footer/>
					</Container>
				</Grid>
			</Grid>
		</Box>
	)
}

export default ConfirmationPage
